"use client";

/**
 * P3-10 · PDP content module — the brand-configurable tab set below the buy-box
 * (P3-DB01). Desktop (≥md) renders the design-system `Tabs`; below md the same
 * panels stack as an accordion (independent disclosures, `aria-expanded`
 * buttons). A tab appears only when it BOTH has content AND is enabled by the
 * brand's `pdp.tabs.*` flags — so a description-only product shows a single
 * panel with no empty tabs, and an empty product renders nothing at all.
 *
 * The rich description arrives pre-sanitised as HTML from the projection
 * (`renderDescriptionHtml`, server-side), so this client component ships no
 * sanitiser and every tag/attribute in it is already escaped/allow-listed.
 */
import * as React from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useTranslator } from "~/i18n/provider";
import type { Translator } from "~/i18n/messages";
import { richTextProseCompact } from "~/lib/rich-text-prose";
import type { PdpProduct } from "~/server/api/schemas/pdp";
import type { PdpTabsConfig } from "~/server/api/schemas/brand-settings";

import { Downloads } from "./Downloads";
import { Faq } from "./Faq";
import { SpecsTable } from "./SpecsTable";

interface Panel {
  id: string;
  label: string;
  content: React.ReactNode;
}

/**
 * Tailwind-scoped styling for the sanitised rich-description HTML. Shared with
 * the blog article body (P8-13) — same sanitiser, same renderer, same styles.
 */
const DESCRIPTION_PROSE = richTextProseCompact;

function buildPanels(
  product: PdpProduct,
  tabs: PdpTabsConfig,
  t: Translator,
): Panel[] {
  const panels: Panel[] = [];

  // Description: rich HTML when present, else the plain-text fallback. No flag.
  if (product.descriptionHtml) {
    panels.push({
      id: "description",
      label: t("pdp.description"),
      content: (
        <div
          className={DESCRIPTION_PROSE}
          dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
        />
      ),
    });
  } else if (product.description) {
    panels.push({
      id: "description",
      label: t("pdp.description"),
      content: (
        <p className="max-w-prose text-sm leading-relaxed text-fg-muted">
          {product.description}
        </p>
      ),
    });
  }

  if (tabs.specs && product.attributes.length > 0) {
    panels.push({
      id: "specifications",
      label: t("pdp.specifications"),
      content: <SpecsTable attributes={product.attributes} />,
    });
  }

  const hasDownloads = product.downloads.some((d) => d.url !== null);
  if (tabs.downloads && hasDownloads) {
    panels.push({
      id: "downloads",
      label: t("pdp.downloads"),
      content: <Downloads downloads={product.downloads} />,
    });
  }

  if (tabs.faq && product.faqs.length > 0) {
    panels.push({
      id: "faq",
      label: t("pdp.faq"),
      content: <Faq faqs={product.faqs} />,
    });
  }

  // P13-16 · No reviews panel here, deliberately.
  //
  // There used to be one: "Reviews are coming soon.", gated on
  // `settings.pdp.tabs.reviews`. It outlived the thing it was waiting for —
  // P5-16 shipped the real `<ReviewsSection>`, which renders below this
  // component under the `reviews` FEATURE FLAG. So a shopper could see a tab
  // promising reviews later, directly above the reviews.
  //
  // Worse, the placeholder claimed `id: "reviews"`, and the real section is
  // linked as `/products/<slug>#reviews` — so the deep link landed on the
  // apology instead of the reviews it points at.
  //
  // Reviews are now owned by exactly one switch: the `reviews` feature flag.

  return panels;
}

/** Resolve the initially-open panel from a `#id` deep-link, else the first. */
function initialPanelId(panels: Panel[]): string {
  if (typeof window !== "undefined") {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash && panels.some((p) => p.id === hash)) return hash;
  }
  return panels[0]?.id ?? "";
}

export function ProductContent({
  product,
  tabs,
}: {
  product: PdpProduct;
  tabs: PdpTabsConfig;
}) {
  const t = useTranslator();
  const panels = React.useMemo(
    () => buildPanels(product, tabs, t),
    [product, tabs, t],
  );

  // Desktop selected tab. Deep-link resolves after mount to keep SSR stable.
  const [active, setActive] = React.useState(() => panels[0]?.id ?? "");
  // Mobile open disclosures (independent — several may be open at once).
  const [open, setOpen] = React.useState<Set<string>>(
    () => new Set(panels[0] ? [panels[0].id] : []),
  );

  React.useEffect(() => {
    const initial = initialPanelId(panels);
    if (initial) {
      setActive(initial);
      setOpen(new Set([initial]));
    }
    // Panels are derived from stable product/tabs; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (panels.length === 0) return null;

  const selectTab = (id: string) => {
    setActive(id);
  };

  const toggleDisclosure = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <section aria-label={t("pdp.productDetails")}>
      {/* Desktop: design-system tabs (≥md). */}
      <div className="hidden md:block">
        <Tabs value={active} onValueChange={selectTab}>
          <TabsList>
            {panels.map((p) => (
              <TabsTrigger key={p.id} value={p.id}>
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {panels.map((p) => (
            <TabsContent key={p.id} value={p.id}>
              {p.content}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Mobile: stacked accordion (<md). */}
      <div className="flex flex-col divide-y divide-border border-y border-border md:hidden">
        {panels.map((p) => {
          const isOpen = open.has(p.id);
          return (
            <div key={p.id}>
              <h3>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`pdp-panel-${p.id}`}
                  onClick={() => toggleDisclosure(p.id)}
                  className="flex w-full items-center justify-between gap-4 py-4 text-left text-sm font-semibold text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                >
                  {p.label}
                  <span aria-hidden="true" className="text-xs text-fg-muted">
                    {isOpen ? "▴" : "▾"}
                  </span>
                </button>
              </h3>
              {isOpen && (
                <div id={`pdp-panel-${p.id}`} className="pb-5">
                  {p.content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
